import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Download, BookOpen, Check } from 'lucide-react';
import { submitGuideOptin, guideDownloadUrl } from '../services/api';
import { toast } from 'sonner';

export default function GuideOptinDialog({ trigger }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) { toast.error('Please enter your email'); return; }
    setLoading(true);
    try {
      await submitGuideOptin(form);
      setDone(true);
      window.open(guideDownloadUrl(form.email), '_blank', 'noopener');
      toast.success('Guide sent, opening a printable copy now.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setDone(false); setForm({ name: '', email: '' }); } }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-[#111116] border-white/10 text-white rounded-none max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 grid place-items-center brand-gradient"><BookOpen className="w-5 h-5 text-white" /></div>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-brand">Free Download</div>
              <DialogTitle className="font-display text-3xl uppercase italic">7 Day Starter Guide</DialogTitle>
            </div>
          </div>
          <DialogDescription className="text-white/60">Drop your email and I'll send over a printable one-pager, training, nutrition and mindset for your first week.</DialogDescription>
        </DialogHeader>
        {!done ? (
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div>
              <label className="text-[11px] uppercase tracking-[0.25em] text-white/60">Name (optional)</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-transparent border-white/10 focus:border-brand rounded-none text-white" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.25em] text-white/60">Email</label>
              <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 bg-transparent border-white/10 focus:border-brand rounded-none text-white" />
            </div>
            <Button disabled={loading} type="submit" className="w-full brand-gradient text-white rounded-none uppercase tracking-widest text-xs py-6 glow-brand">
              <Download className="w-4 h-4 mr-2" /> {loading ? 'Sending...' : 'Send me the guide'}
            </Button>
            <p className="text-white/40 text-xs">One email. No spam. Unsubscribe anytime.</p>
          </form>
        ) : (
          <div className="text-center py-6">
            <div className="w-14 h-14 rounded-full brand-gradient grid place-items-center mx-auto"><Check className="w-6 h-6 text-white" /></div>
            <div className="font-display uppercase text-2xl mt-4">You're in.</div>
            <p className="text-white/60 mt-2">A printable copy just opened in a new tab.</p>
            <a href={guideDownloadUrl(form.email)} target="_blank" rel="noreferrer">
              <Button variant="outline" className="mt-5 bg-transparent border-white/25 text-white hover:bg-white hover:text-black uppercase tracking-widest text-xs rounded-none px-6 py-5">
                <Download className="w-4 h-4 mr-2" /> Open again
              </Button>
            </a>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
